import { Component, computed, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { RsvpService } from '../../../services/rsvp-service';
import { form, FormField, required } from '@angular/forms/signals';
import { RsvpData } from '../../../models/RsvpData';
import { AttendingStatus } from '../../../models/AttendingStatus.enum';
import { Person } from '../../../models/Person';
import { RsvpDataResponse } from '../../../dto/RsvpDataResponse';
import { AuthService } from '../../../services/auth-service';
import { ToastrService } from 'ngx-toastr';
import { InvitationType } from '../../../models/InvitationType.enum';
import { v4 as uuid } from 'uuid';
import { Hero } from '../../common/hero/hero';

@Component({
  selector: 'app-rsvp-page',
  imports: [FormField, Hero],
  templateUrl: './rsvp-page.html',
  styleUrl: 'rsvp-page.scss',
})
export class RsvpPage implements OnInit {

  private rsvpService = inject(RsvpService);
  private authService = inject(AuthService);
  private toastrService = inject(ToastrService);

  canRequireChildsInvite = signal(false);
  canRequireCoupleInvite = signal(false);
  canRequireBrunch = signal(false);

  showLoader = signal(false);
  showError = signal(false);
  isVhOnly = signal(true);
  hasAlreadyAnswered = signal(false);
  showPasscode = signal(false);
  showPasscodeError = signal(false);

  attendingStatusOptions = [
    AttendingStatus.ATTENDING,
    AttendingStatus.NOT_ATTENDING,
    AttendingStatus.NOT_SURE_YET,
  ];

  rsvpDataModel = signal<RsvpData>({
    person1: { firstName: "", lastName: "", adult: true, personId: "" },
    person2: undefined,
    childs: [],
    attendingStatus: '',
    comment: '',
    bonusQuestionAnswer: '',
    staysForBrunch: false,
  });

  randomPlaceholder = computed(() => {
    const placeholders = [
      "Evidement Maëlyse, déguisée en Schtroumpfette !",
      "Evidement Maëlyse, qui mange un carotte comme Bugs Bunny !",
      "Evidement Maëlyse, en train de négocier avec un goéland pour récupérer ses churros !",
      "Evidement Maëlyse, en train de prendre sa 47e photo du coucher de soleil, parce que « la lumière n'est pas tout à fait la bonne » !",
      "Evidement Maëlyse, en train de hurler sur une araignée qui vient de traverser la terrasse, comme si elle venait de croiser Aragog !",
      "Evidement Aurélien, en train de ronfler, le dos rouge-vif, pendant une sieste au soleil !",
      "Evidement Aurélien, en train d'attaquer une noix de coco à grand coups de couteau à beurre !",
      "Evidement Aurélien, très sérieux, en train de construire un château de sable géant !",
      "Evidement Aurélien, en train de chanter devant un miroir avec une brosse à cheveux !",
      "Evidement Aurélien, tirant 2 grosses valises sur des pavés en pente, essouflé, mais jurant que c'est « Juste à 2 minutes ! »",
    ];
    const randomIndex = Math.floor(Math.random() * placeholders.length);
    return placeholders[randomIndex];
  });

  rsvpDataForm = form(this.rsvpDataModel, (schemaPath) => {
    required(schemaPath.person1.firstName, { message: 'First name is required' });
    required(schemaPath.person1.lastName, { message: 'Last name is required' });
  });

  isFormDirty = computed(() => {
    return this.rsvpDataForm().dirty();
  });

  passcode: WritableSignal<string> = signal('');
  passcodeForm = form(this.passcode);

  ngOnInit(): void {
    this.showLoader.set(true);
    setTimeout(() => {
      if (this.authService.currentFamilyId() === '') {
        this.showPasscode.set(true);
        this.showLoader.set(false);
      } else {
        this.retrieveRsvpData()
        this.showPasscode.set(false);
      }
    }, this.authService.currentFamilyId() === '' ? 250 : 0);
  }

  private retrieveRsvpData(): void {
    this.showLoader.set(true);
    this.rsvpService.getRsvpData(this.authService.currentFamilyId()).then(response => {
      this.updateRsvpFormData(response);
      this.showError.set(false);
    }).catch(() => {
      this.showError.set(true);
      this.toastrService.error('Erreur de recuperation des données');
    }).finally(
      () => this.showLoader.set(false)
    );
  }


  private updateRsvpFormData(response: RsvpDataResponse): void {
    let person1: Person;
    let person2: Person | undefined;

    let childs: Person[] = [];

    response.persons.forEach((person: Person) => {
      if (person.adult && !person1) {
        person1 = person;
      } else if (person.adult && !person2) {
        person2 = person;
      } else {
        childs.push(person);
      }
    });

    let attendingStatus = response.attendingStatus;

    this.rsvpDataModel.set({
      person1: { ...person1!, lastName: person1!.lastName.toUpperCase() },
      person2: person2 ? { ...person2, lastName: person2!.lastName.toUpperCase() } : undefined,
      childs: childs?.map(child => ({ ...child, lastName: child.lastName.toUpperCase() })),
      attendingStatus: attendingStatus,
      comment: response.comment,
      bonusQuestionAnswer: response.bonusQuestionAnswer,
      staysForBrunch: response.staysForBrunch
    });

    this.canRequireChildsInvite.set(response.canRequireChildsInvite);
    this.canRequireCoupleInvite.set(response.canRequireCoupleInvite);
    this.isVhOnly.set(response.invitationType === InvitationType.VH.getKey() || response.invitationType === InvitationType.VH_PARENTS.getKey());
    this.hasAlreadyAnswered.set(attendingStatus !== AttendingStatus.NOT_RESPONDED.getKey());

    this.rsvpDataForm().reset();
  }

  validate(): void {
    if (this.rsvpDataForm().dirty()) {
      this.rsvpService.saveRsvpData(this.authService.currentFamilyId(), this.rsvpDataModel()).then(response => {
        this.updateRsvpFormData(response);
        this.rsvpDataForm().reset();
        this.toastrService.success('Mise à jour validée !');
      }).catch(() => this.toastrService.error('Erreur de mise à jour des données'));
    }
  }

  addAdult(): void {
    this.rsvpDataModel.update(data => ({
      ...data,
      ['person2']: { firstName: "", lastName: "", adult: true, personId: uuid().toString() },
    }));
    this.rsvpDataForm().markAsDirty();
  }

  addChild(): void {
    this.rsvpDataModel.update(data => ({
      ...data,
      childs: [...data.childs!, { firstName: "", lastName: "", adult: false, personId: uuid().toString() }],
    }));
    this.rsvpDataForm().markAsDirty();
  }

  removeAdult(key: 'person1' | 'person2'): void {
    if (key === 'person1') {
      this.rsvpDataModel.update(data => ({
        ...data,
        ['person1']: this.rsvpDataModel().person2!,
      }));
    }

    this.rsvpDataModel.update(data => ({
      ...data,
      ['person2']: undefined,
    }));
    this.rsvpDataForm().markAsDirty();
  }

  removeChild(index: number): void {
    this.rsvpDataModel.update(data => ({
      ...data,
      childs: data.childs!.filter((_, i) => i !== index),
    }));
    this.rsvpDataForm().markAsDirty();
  }

  resetForm(): void {
    this.retrieveRsvpData();
    this.toastrService.info("Changement(s) éffacé(s)");
  }

  validatePasscode(): void {
    if (this.passcode().trim().length === 0) {
      this.showPasscodeError.set(true);
      return;
    } else {
      this.showPasscodeError.set(false);
      this.showLoader.set(true);
      this.authService.tryPasscodeLogin(this.passcode()).then(response => {
        if (response.success) {
          this.retrieveRsvpData();
          this.showPasscode.set(false);
        } else {
          this.showPasscodeError.set(true);
        }
        this.showLoader.set(false);
      });
    }
  }
}
